import { Err, Ok, Result } from 'ts-res';
import { FeatureVariableType, JSONType } from 'src/clientConfiguration';
import {
  KameleoonError,
  KameleoonException,
  exhaustCheck,
} from 'src/kameleoonError';
import { FeatureVariableResultType, VariableType } from 'src/types';
import {
  ParseVisitorDataParametersType,
  ParseVisitorDataResultType,
} from './types';
import { VisitProcessor } from 'src/kameleoonData/visitProcessor';
import {
  HIDDEN_SECRET_CHAR,
  SECRET_VISIBLE_LENGTH,
} from 'src/utilities/constants';
import { VisitsData } from 'src/kameleoonData/visitsData';

export class Parser {
  static parseFeatureVariable(
    variable: FeatureVariableType,
  ): Result<FeatureVariableResultType, KameleoonError> {
    const { key, type, value } = variable;

    switch (type) {
      case VariableType.BOOLEAN:
        return Ok({
          key,
          type,
          value: Boolean(value),
        });
      case VariableType.JS:
      case VariableType.CSS:
      case VariableType.STRING:
        return Ok({
          key,
          type,
          value: String(value),
        });
      case VariableType.NUMBER:
        const numberValue = Number(value);

        if (Number.isNaN(numberValue)) {
          return Err(
            new KameleoonError(KameleoonException.NumberParse, String(value)),
          );
        }

        return Ok({
          key,
          type,
          value: numberValue,
        });
      case VariableType.JSON:
        try {
          const jsonValue: JSONType = JSON.parse(String(variable.value));

          return Ok({
            key,
            type,
            value: jsonValue,
          });
        } catch (err) {
          return Err(new KameleoonError(KameleoonException.JSONParse, err));
        }
      default:
        exhaustCheck(type);
    }
  }

  static parseVisitorData({
    data,
    filters,
    visitorCode,
    dataManager,
    variationConfiguration,
  }: ParseVisitorDataParametersType): ParseVisitorDataResultType {
    const visitProcessor = new VisitProcessor({
      filters,
      dataManager,
      visitorCode,
    });

    const { currentVisit, previousVisits, kcs, cbs } = data;

    if (!previousVisits?.length && !currentVisit) {
      return {
        visitorData: [],
        storageVisitorData: [],
        visitsData: new VisitsData([]),
      };
    }

    if (currentVisit) {
      visitProcessor.processVisit(currentVisit);
      visitProcessor.processVisitNumber(currentVisit, 0);
    }

    previousVisits?.forEach((visit, index) => {
      visitProcessor.processVisit(visit);
      visitProcessor.processVisitsData(visit);
      visitProcessor.processVisitNumber(visit, index + 1);
    });

    visitProcessor.processKcs(kcs);
    visitProcessor.processCbs(cbs);
    visitProcessor.processMultipleDataTypes();
    const mappingIdentifier = visitProcessor.processMappingIdentifier(
      currentVisit ?? previousVisits?.[0],
    );

    const { visitorData, storageVisitorData, visitsData, experimentData } =
      visitProcessor.data;

    variationConfiguration.updateStoredVariations(visitorCode, experimentData);

    return {
      visitorData,
      storageVisitorData,
      visitsData,
      mappingIdentifier,
    };
  }

  static parseRegExp(value: string): RegExp {
    if (value[0] === '/') {
      const [_, expression, flags] = value.split('/');

      return new RegExp(expression, flags);
    }

    return new RegExp(value);
  }

  static parseSecret(secret: string | null): string {
    if (secret === null) {
      return 'null';
    }

    const length = secret.length;

    if (length <= SECRET_VISIBLE_LENGTH) {
      return HIDDEN_SECRET_CHAR.repeat(length);
    }

    const hiddenLength = Math.max(
      length - SECRET_VISIBLE_LENGTH,
      SECRET_VISIBLE_LENGTH,
    );

    return (
      secret.substring(0, length - hiddenLength) +
      HIDDEN_SECRET_CHAR.repeat(hiddenLength)
    );
  }
}
